import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";

/**
 * Output shape of OpenAI's provider-executed web search tool, as surfaced by
 * the AI SDK (`mapWebSearchOutput`). The model can make several web_search_call
 * actions in one research burst, and each arrives as its own tool part, so a
 * single burst renders as several of these rows (a search, then any pages it
 * opened). Only the fields we render are typed, and all are optional because
 * the payload varies by action type.
 */
interface WebSearchToolOutput {
  action?: {
    type?: "search" | "openPage" | "findInPage";
    query?: string;
    queries?: readonly string[];
    url?: string | null;
  };
}

const MAX_SHOWN_QUERIES = 3;

export const WebSearchTool: ToolComponent<unknown, WebSearchToolOutput> = (
  props,
) => (
  <ChatToolBase status={props.status}>
    {renderLabel(props.status.type, props.output)}
  </ChatToolBase>
);

function renderLabel(
  statusType: "running" | "complete" | "incomplete",
  output: WebSearchToolOutput | undefined,
): string {
  if (statusType !== "complete") {
    return "Searching the web...";
  }

  const action = output?.action;

  if (action?.type === "openPage") {
    const host = tryGetHost(action.url);
    return host ? `Read ${host}` : "Read a web page";
  }

  if (action?.type === "findInPage") {
    return "Searched within a web page";
  }

  const queries = collectQueries(action);
  if (queries.length > 0) {
    const shown = queries
      .slice(0, MAX_SHOWN_QUERIES)
      .map((query) => `"${query}"`)
      .join(", ");
    const extra = queries.length - MAX_SHOWN_QUERIES;
    return extra > 0
      ? `Searched the web: ${shown} +${extra} more`
      : `Searched the web: ${shown}`;
  }

  return "Searched the web";
}

function collectQueries(
  action: WebSearchToolOutput["action"],
): readonly string[] {
  if (!action) {
    return [];
  }
  if (action.queries && action.queries.length > 0) {
    return action.queries.filter((query) => query.trim().length > 0);
  }
  return action.query?.trim() ? [action.query] : [];
}

function tryGetHost(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}
